// L'app offerte par un partenaire (D-084, révisé par D-147 : maquettes o0 à o4 et v1 à v3 validées) :
//  - espace du partenaire (son lien privé ou son compte, « vous ») : tant qu'aucun partenaire ne l'offre pour la saison,
//    « Offrez l'app au <club> » d'office (jamais un partenaire alcool ou jeux d'argent : la base ne le propose pas), au
//    prix de l'abonnement de la saison ; « J'offre l'app au club » ouvre la promesse de don (« Je m'engage ») ; ensuite,
//    « Partenaire officiel de l'app », merci, sa promesse et son reçu fiscal à venir ;
//  - bureau (Partenaires) : l'état simple de la saison — « Proposée à tes N partenaires », « Offerte par X · don promis »
//    (« Noter le règlement reçu », « Retirer »), « · don reçu » ; non réglée après 60 jours : relancer ou retirer la ligne ;
//  - accueil de chaque membre, écran de connexion de l'app du club, page publique et site du club : « L'app du club est
//    offerte par… », aussitôt (pas de confirmation du bureau), sobre : le nom et le logo, sans slogan ni lien vers son site.
// Le club paie Arceau ; l'argent entre le club et le partenaire passe hors de l'app (le don, D-124).
import { clubOfficialPartner, type Partner } from '../lib/api';
import {
  clubOfferedBy, officialPledge, officialPledgeRemind, officialPledgeWithdraw, partnerAppOffer, type AppOffer, type OfferAdmin,
} from '../lib/lien-api';
import { euros } from '../domain/licences';
import { daysBetween, dayMonth, pledgeLate } from '../domain/lien';
import { withArticle } from '../../supabase/functions/_shared/partner-mail';
import { isMinorAccount, type Ctx } from './context';
import { $, confirmTwice, html, mount, openSheet, toast, type Html } from './dom';
import { partnerMark } from './partners';

/** « saison 2026-2027 » d'un seul tenant (pas de coupure au trait d'union dans un sur-titre). */
const seasonTag = (name: string) => html`<span class="po-nw">saison ${name}</span>`;
/** « au Basket Club de Rivebelle », « à l’ASC Valdorne », « à la Jeanne d’Arc », « à Lou Basket ». */
export function auClub(name: string): string {
  const a = withArticle(name);
  return a.startsWith('le ') ? `au ${a.slice(3)}` : `à ${a}`;
}
/** « aujourd’hui », « hier », « le 1er nov. ». */
const dayWord = (iso: string, today: string) => { const n = daysBetween(iso.slice(0, 10), today); return n <= 0 ? 'aujourd’hui' : n === 1 ? 'hier' : `le ${dayMonth(iso.slice(0, 10))}`; };

/** La ligne sobre « L'app du club est offerte par <logo> <partenaire> › » (jamais un lien vers son site). */
export function offeredLine(p: { id?: string; name: string; logo: string | null }, o: { href?: string | null; chevron?: boolean } = {}): Html {
  const body = html`${partnerMark({ name: p.name, logo: p.logo }, 'pt-logo po-mark')}
    <span class="po-text">L’app du club est offerte par <strong>${p.name}</strong></span>
    ${(o.chevron ?? !!o.href) ? html`<span class="occ-go" aria-hidden="true">›</span>` : ''}`;
  return o.href ? html`<a class="po-line" href="${o.href}">${body}</a>` : html`<div class="po-line">${body}</div>`;
}

/** Accueil d'un membre : la ligne, dès qu'un partenaire offre l'app (jamais pour un compte mineur). */
export async function renderOfficialLine(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club || isMinorAccount(ctx)) return;
  const o = await clubOfficialPartner(ctx.club.id).catch(() => null);
  mount(el, o ? offeredLine({ name: o.name, logo: o.logo_url }, { href: '#/partenaires' }) : html``);
}

/** L'écran de connexion de l'app du club (sans compte) : la ligne, sous le nom du club (maquette v3). */
export async function renderLoginLine(el: HTMLElement, brand: { id: string } | null): Promise<void> {
  if (!brand) return;
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(brand.id) ? brand.id : null;
  const slug = /^host:([a-z0-9-]{3,40})$/.exec(brand.id)?.[1] ?? null;
  if (!uuid && !slug) return;
  const o = await clubOfferedBy(uuid, slug).catch(() => null);
  if (o && el.isConnected) mount(el, offeredLine({ name: o.name, logo: o.logo }));
}

// --- Bureau : écran Partenaires (maquettes o0, o4) -----------------------------------------------------
/** L'état de la saison : proposée d'office, offerte (don promis), don reçu. Vide sans saison active. */
export function offerAdminBlock(a: OfferAdmin | null, partners: Partner[]): Html {
  if (!a?.season) return html``;
  const o = a.offer && a.offer.status === 'confirme' && a.offer.visible ? a.offer : null;
  const head = html`<p class="eyebrow">L’app offerte par un partenaire · ${seasonTag(a.season.name)}</p>`;
  if (!o) {
    const eligible = partners.filter(p => !p.restricted && !p.archived_at);
    const evin = partners.filter(p => p.restricted && !p.archived_at).map(p => p.name);
    const price = a.price_cents;
    return html`<section class="card po-done" id="po-state" data-guide="po-state">${head}
      <div class="po-state"><span class="po-ic" aria-hidden="true">✦</span>
        <span class="pick-main"><strong>${eligible.length ? `Proposée à tes ${eligible.length} ${eligible.length > 1 ? 'partenaires' : 'partenaire'}` : 'Proposée à tes partenaires'}</strong>
          <span class="muted small">${price ? `D’office, dans leur espace : « Offrez l’app au club » · ${euros(price)} pour la saison. Rien à envoyer.`
            : 'Au-delà de 1 000 licenciés, le prix se fait sur devis : la proposition attend le prix de la saison.'}</span></span>
        <span class="chip chip-mute">En attente</span></div>
      <p class="hint">${evin.length ? `Jamais aux partenaires alcool ou jeux d’argent (loi Évin) : ${evin.join(', ')} ne la ${evin.length > 1 ? 'voient' : 'voit'} pas. ` : 'Jamais aux partenaires alcool ou jeux d’argent (loi Évin). '}Le premier qui s’engage l’offre ; la proposition disparaît alors des autres espaces.</p>
    </section>`;
  }
  const paid = !!o.paid_at;
  const late = !paid && o.pledge && pledgeLate(o.pledged_at, a.today);
  const days = o.pledged_at ? daysBetween(o.pledged_at.slice(0, 10), a.today) : 0;
  const receipt = o.receipt === 'a_signer' ? 'reçu fiscal à signer' : o.receipt === 'signe' ? 'reçu fiscal signé' : o.receipt === 'envoye' ? 'reçu fiscal envoyé' : '';
  return html`<section class="card po-done" id="po-state" data-guide="po-state">${head}
    <div class="po-state">${partnerMark({ name: o.name, logo: o.logo }, 'pt-logo po-mark-md')}
      <span class="pick-main"><strong>Offerte par ${o.name} · ${paid ? 'don reçu' : 'don promis'}</strong>
        <span class="muted small">${[`${euros(o.amount_cents)} pour la saison`,
          paid ? `reçu ${dayWord(o.paid_at!, a.today)}` : o.pledged_at ? `promis${o.pledged_by ? ` par ${o.pledged_by}` : ''} ${dayWord(o.pledged_at, a.today)}` : '',
          paid ? receipt : 'la ligne est en ligne'].filter(Boolean).join(' · ')}</span></span>
      <span class="chip ${paid ? 'chip-ok' : 'chip-warn'}">${paid ? 'Don reçu' : 'Don promis'}</span></div>
    ${offeredLine({ name: o.name, logo: o.logo }, { chevron: true })}
    ${late ? html`<p class="small po-late">Promis il y a ${days} jours, pas encore réglé : ${o.reminded_at ? `relancé ${dayWord(o.reminded_at, a.today)} ; tu peux retirer la ligne.` : 'relance-le, ou retire la ligne.'}</p>` : ''}
    <div class="btn-row">
      ${!paid && o.pledge ? html`<button type="button" class="btn btn-primary btn-sm" id="po-paid" data-guide="po-paid">Noter le règlement reçu</button>` : ''}
      ${late && !o.reminded_at ? html`<button type="button" class="btn btn-ghost btn-sm" id="po-remind">Relancer</button>` : ''}
      <button type="button" class="btn btn-link btn-sm" id="po-withdraw">${late ? 'Retirer la ligne' : 'Retirer'}</button></div>
    <p class="hint">${paid ? 'Le don est noté « reçu » dans les dons du club, avec son reçu fiscal.'
      : `Le don est noté « promis » dans les dons du club. Noter le règlement le passe à « reçu » et ouvre le reçu fiscal : « Offerte par ${o.name} · don reçu ».`}</p>
  </section>`;
}

/** « Noter le règlement reçu » : le don de D-124, le montant promis prérempli (la fiche du partenaire et Partenaires). */
export function openPledgePaid(ctx: Ctx, a: OfferAdmin, partners: { id: string; contact_email: string | null; contact_name: string | null }[], reload: () => void): void {
  const o = a.offer;
  if (!o || !a.season) return;
  const p = partners.find(x => x.id === o.partner_id);
  void import('./recus').then(({ openGiftSheet }) => openGiftSheet(ctx,
    { id: o.partner_id, name: o.name, contact_email: p?.contact_email ?? null, contact_name: p?.contact_name ?? null }, reload, null,
    { amount_cents: o.amount_cents, season: a.season!.name }));
}

/** Les gestes du bloc : noter le règlement (le don, D-124), relancer, retirer la ligne. */
export function wireOfferAdmin(root: HTMLElement, ctx: Ctx, clubId: string, a: OfferAdmin | null, partners: Partner[], reload: () => void): void {
  const o = a?.offer;
  if (!a?.season || !o) return;
  $(root, '#po-paid')?.addEventListener('click', () => openPledgePaid(ctx, a, partners, reload));
  $(root, '#po-remind')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try {
      const r = await officialPledgeRemind(clubId);
      toast(`Relance envoyée à ${o.name}${r.mailed && r.notified ? ' : un e-mail et une notification' : r.mailed ? ' par e-mail' : ' dans son espace'}`);
      reload();
    } catch (x) { b.disabled = false; toast(String((x as Error).message).includes('ni e-mail') ? 'Ni e-mail ni compte : appelle-le.' : 'Relance impossible pour le moment.', 'ko'); }
  });
  const w = $<HTMLButtonElement>(root, '#po-withdraw');
  if (w) confirmTwice(w, 'Confirmer : retirer la ligne', async () => {
    try { await officialPledgeWithdraw(clubId); toast('Ligne retirée : l’app est de nouveau proposée dans chaque espace partenaire'); reload(); }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
}

// --- Espace du partenaire (maquettes o1 à o3) --------------------------------------------------------
/** « Offrez l'app au <club> » d'office, ou « Partenaire officiel de l'app » s'il l'offre. Rien sinon. */
export function offerCard(o: AppOffer | null, partner: { name: string; logo: string | null }, clubName: string, who: string | null): Html {
  if (!o) return html``;
  if (o.state === 'mine') return html`
    <section class="card po-preview" data-guide="pa-offer">
      <p class="pub-kicker">Partenaire officiel de l’app · ${seasonTag(o.season)}</p>
      ${offeredLine(partner, { chevron: true })}
      <p class="small"><strong>Merci${who ? ` ${who}` : ''} !</strong> Votre logo est en tête de l’accueil de chaque membre, et sur le site du club, toute la saison.</p>
      <p class="muted small">${o.paid ? `Votre don de ${euros(o.amount_cents)} est noté par le club : merci ! Votre reçu fiscal arrive ici dès qu’il est signé.`
        : `Votre promesse de don : ${euros(o.amount_cents)} · le club vous dit comment le régler ; votre reçu fiscal arrivera ici.`}</p>
    </section>`;
  return html`
    <section class="pr-ask po-offer" data-guide="pa-offer">
      <p class="pub-kicker">Saison ${o.season} · proposé à tous les partenaires du club</p>
      <h2>Offrez l’app ${auClub(clubName)}</h2>
      <p>L’app réunit ${o.members > 0 ? `ses ${o.members} licenciés` : 'ses licenciés'} et leurs familles : matchs, convocations, bénévolat.</p>
      <ul class="po-ul">
        <li>Votre logo en tête de l’accueil de chaque membre, toute la saison</li>
        <li>« L’app du club est offerte par ${partner.name} » sur le site du club</li>
        <li>${euros(o.amount_cents)} pour la saison : un don au club (le prix de l’app), qui vous remet son reçu fiscal</li>
      </ul>
      <div class="btn-row"><button type="button" class="btn pr-ask-btn" id="po-offer">J’offre l’app au club</button></div>
    </section>
    <section class="card po-preview"><p class="pub-kicker">Aperçu dans l’app des membres</p>${offeredLine(partner, { chevron: true })}</section>`;
}

/** Le texte exact de la promesse (D-147) : « En validant, vous vous engagez à faire un don de X € au <club> pour la saison …
 *  (promesse de don). » */
export const promiseText = (amountCents: number, clubName: string, season: string) =>
  `En validant, vous vous engagez à faire un don de ${euros(amountCents)} ${auClub(clubName)} pour la saison ${season} (promesse de don).`;

/** « J'offre l'app au club » : la promesse de don (maquette o2), « Je m'engage ». */
export function wireOffer(root: HTMLElement, token: string, o: AppOffer | null, partner: { name: string }, clubName: string, again: () => void): void {
  if (!o || o.state !== 'open') return;
  const club = auClub(clubName);
  $(root, '#po-offer')?.addEventListener('click', () => openSheet('Offrir l’app au club', html`
    <div class="form">
      <div class="po-promise" data-guide="pa-promise"><p class="eyebrow">Promesse de don · ${seasonTag(o.season)}</p><p class="po-big">${euros(o.amount_cents)}</p>
        <p>En validant, vous vous engagez à faire un don de <strong>${euros(o.amount_cents)}</strong> ${club.slice(0, club.length - clubName.length)}<strong>${clubName}</strong> pour la saison ${o.season} (promesse de don).</p></div>
      <ul class="po-ul">
        <li>Tout de suite : « L’app du club est offerte par ${partner.name} » en tête de l’accueil de chaque membre et sur le site du club.</li>
        <li>Le club vous dit comment régler (virement ou chèque) : Arceau n’encaisse rien et ne vous facture rien.</li>
        <li>Votre reçu fiscal arrive dans votre espace dès que le club a noté le règlement.</li>
      </ul>
      <button type="button" class="btn btn-primary" id="po-pledge">Je m’engage</button>
      <p class="hint">${euros(o.amount_cents)}, c’est le prix TTC de l’app pour la saison. Un seul partenaire l’offre : la proposition disparaît alors des autres espaces.</p>
      <p class="form-msg" role="alert"></p>
    </div>`, (sheet, close) => {
    $(sheet, '#po-pledge')!.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      try { await officialPledge(token); close(); toast('Merci ! La ligne est en ligne, le club est prévenu'); again(); }
      catch (x) {
        const m = String((x as Error).message);
        $(sheet, '.form-msg')!.textContent = m.includes('déjà offerte') ? 'Un autre partenaire vient de l’offrir pour cette saison : merci quand même !'
          : 'Engagement impossible pour le moment. Réessayez.';
        if (!m.includes('déjà offerte')) b.disabled = false;
      }
    });
  }));
}

/** L'espace du partenaire : la proposition (ou la sienne) ; null si rien à montrer. */
export const loadOffer = (token: string) => partnerAppOffer(token).catch(() => null);
